import React, { useState, useEffect, useMemo } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { useSelector, useDispatch } from 'react-redux';
import CustomTitleAndSubTitle from '../myOrders/customTitleAndSubTitle';
import CustomBannerShipment from './customShipmentBanner';
import { useTranslation } from 'react-i18next';
import CustomInfoInput from '../accountUser/customInfoInput';
import { fetchMyCarts } from '../../redux/reducer/cartReducer';
import { checkShipmentCostPay } from '../../redux/reducer/shipmentCostReducer';
import LoadingSpinner from '../animation/LoadingSpinner';
import { getCurrencyAndLanguageSymbol } from '../../constent/currencyUtils';
import CustomShipmentBannerTotal from './customShipmentBannerTotal';
import { getMenuItemsLocationTo } from '../../constent/countries';

const ManageBoxDetails = ({ cartIds, getCompanyObject }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { carts } = useSelector((state) => state.carts);
  const [getCompany, setGetCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [shipmentCosts, setShipmentCosts] = useState({});
  const [isCartsFetched, setIsCartsFetched] = useState(false);
  const currencySymbol = getCurrencyAndLanguageSymbol();
  const menuItemsLocationTo = getMenuItemsLocationTo(t);

  const roundToTwo = (num) => parseFloat(Number(num).toFixed(2));

  const roundWeight = (weight) => {
    if (isNaN(weight) || weight <= 0) return 0;
    const integerPart = Math.floor(weight);
    const decimalPart = weight - integerPart;
    if (decimalPart <= 0.25) return integerPart;
    else if (decimalPart <= 0.75) return integerPart + 0.5;
    else return integerPart + 1;
  };

  // Fetch latest carts on mount or cartIds change
  useEffect(() => {
    const fetchCarts = async () => {
      setLoading(true);
      try {
        await dispatch(fetchMyCarts()).unwrap();
        setIsCartsFetched(true);
      } catch (error) {
        console.error("Error fetching updated carts:", error);
      } finally {
        setLoading(false);
      }
    };

    if (cartIds?.length > 0) {
      fetchCarts();
    }
  }, [dispatch, cartIds]);

  const selectedCarts = useMemo(() => {
    return carts.filter((cart) => cartIds.includes(cart._id));
  }, [carts, cartIds]);

  const totalWeight = selectedCarts.reduce((acc, c) => {
    const weight = c.isPacking ? c.totalWeightAfterPacking : c.totalWeight;
    return acc + (weight || 0);
  }, 0);

  const mergedCart = {
    referenceCode: selectedCarts.map((c) => c.referenceCode).join(', '),
    area: selectedCarts[0]?.area,
    products: selectedCarts.flatMap((c) => c.products || []),
    totalShipment: selectedCarts.reduce((acc, c) => acc + (c?.shippingCompany?.countryData?.cost || 0), 0),
    totalWeight: totalWeight,
    totalPackingFees: selectedCarts.reduce((acc, c) => acc + (c.totalPackingFees || 0), 0),
    address: selectedCarts[0]?.address || {},
    countOfProductsShipped: selectedCarts.reduce((acc, c) => acc + (c.countOfProductsShipped || 0), 0),
  };

  getCompanyObject(getCompany);

  // Fetch shipment costs after carts are fetched
  useEffect(() => {
    if (!isCartsFetched || !selectedCarts.length) return;

    const fetchAllShipmentCosts = async () => {
      setLoading(true);
      setShipmentCosts({});
      const costs = {};

      for (const cart of selectedCarts) {
        const rawWeight = cart.isPacking ? cart.totalWeightAfterPacking : cart.totalWeight;
        if (!cart?.address?.country || !rawWeight) continue;

        const roundedWeight = roundWeight(rawWeight);
        const shipmentData = {
          weight: roundedWeight,
          zone: cart.area === "67dfef4a100f542a2c700c04" ? "Turkey" : "Europe",
          country: i18n.getResource("en", "translation", `countries.${cart?.address?.country}`),
        };

        try {
          const result = await dispatch(checkShipmentCostPay(shipmentData)).unwrap();
          if (result) {
            costs[cart._id] = result;
          }
        } catch (error) {
          console.error(`Error fetching shipment cost for cart ${cart._id}:`, error);
        }
      }

      setShipmentCosts(costs);
      setLoading(false);
    };

    fetchAllShipmentCosts();
  }, [dispatch, isCartsFetched, selectedCarts, i18n]);

 

  if (Object.keys(shipmentCosts).length === 0 || Object.keys(shipmentCosts).length !== selectedCarts.length) {
    return (
      <p className='flex justify-center py-4 text-black'>
        {t("AddCompanyPrice")}
      </p>
    );
  }

  else if (loading || !isCartsFetched || Object.keys(shipmentCosts).length !== selectedCarts.length) return <LoadingSpinner />;
  else if (!selectedCarts.length) return null;
  
  return (
    <div className='mt-4'>
      {/* Desktop */}
      <div className="hidden p-2 bg-white border border-gray-500 md:block">
        <CustomShipmentBannerTotal cartNumbers={mergedCart?.referenceCode}  />
        <div className="justify-between p-4 md:flex">

          <div className='flex flex-col w-1/3 gap-4'>
            <CustomTitleAndSubTitle title={t("ProductsCount")} subTitle={mergedCart?.products?.length} />
            <CustomTitleAndSubTitle title={t("PackagePrice")} subTitle={`${roundToTwo(Number(mergedCart?.totalPackingFees))}`} currency={currencySymbol} />
            
          </div>
          <div className='flex flex-col w-1/3 gap-4'>
            <CustomTitleAndSubTitle title={t("TotalWeight")} subTitle={`${roundToTwo(Number(mergedCart?.totalWeight))}`} currency={t("kg")} />
            <CustomTitleAndSubTitle title={t("shipment_price")} subTitle={`${roundToTwo(Number(mergedCart?.totalShipment) || 0)}`} currency={currencySymbol} />
          </div>
          <div className='flex flex-col gap-4'>
            <CustomTitleAndSubTitle title={t("TotalPrice")} subTitle={`${roundToTwo(Number(mergedCart?.totalPackingFees) +Number(mergedCart?.totalShipment))}`} currency={currencySymbol} />
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="block p-2 bg-white border border-gray-500 md:hidden">
        <CustomShipmentBannerTotal cartNumbers={mergedCart?.referenceCode}  />
      
        <div className='flex justify-between mt-4'>   
          <div className='flex flex-col w-1/2 gap-2'>
            <CustomTitleAndSubTitle title={t("ProductsCount")} subTitle={mergedCart?.products?.length} />
            <CustomTitleAndSubTitle title={t("shipment_price")} subTitle={`${roundToTwo(Number(mergedCart?.totalShipment) || 0)}`} currency={currencySymbol} />
            <CustomTitleAndSubTitle title={t("TotalPrice")} subTitle={`${roundToTwo(Number(mergedCart?.totalPackingFees) + Number(mergedCart?.totalShipment))}`} currency={currencySymbol} />
          </div>
          <div className='flex flex-col gap-2'>
          <CustomTitleAndSubTitle title={t("TotalWeight")} subTitle={`${roundToTwo(Number(mergedCart?.totalWeight))} ${t("kg")}`} />
          <CustomTitleAndSubTitle title={t("PackagePrice")} subTitle={`${roundToTwo(Number(mergedCart?.totalPackingFees))} ${currencySymbol}`} />
          </div>
        </div>
      </div>

      
      
      <div className='flex flex-col gap-4 mt-6 lg:gap-6'>
        {selectedCarts.map((cart, index) => (
          <div key={cart._id || index}>
            {/* Desktop */}
            <div className="hidden p-2 bg-white border border-gray-500 md:block">
              <CustomBannerShipment orderNumber={cart?.referenceCode} cartID={cart._id} company={cart.shippingCompany} companiesChoose={shipmentCosts[cart._id]?.data} getCompany={setGetCompany} loading={setLoading}/>

              <div className="justify-between p-4 md:flex">
                <div className='flex flex-col w-1/3 gap-4'>
                  <CustomTitleAndSubTitle title={t("from")} subTitle={cart?.area === "67dfef4a100f542a2c700c04" ? t("Turkey") : t("Europa")} />
                </div>
                <div className='flex flex-col w-1/3 gap-4'>
                  {cart.isPacking ?
                    <CustomTitleAndSubTitle title={t("WeightAfterPackaging")} subTitle={`${roundToTwo(Number(cart?.totalWeightAfterPacking))}`} currency={t("kg")} />
                    :
                    <CustomTitleAndSubTitle title={t("weight")} subTitle={`${roundToTwo(Number(cart?.totalWeight))}`} currency={t("kg")} />
                  }
                </div>
                <div className='flex flex-col gap-4'>
                  <CustomTitleAndSubTitle title={t("PackagePrice")} subTitle={`${roundToTwo(Number(cart?.totalPackingFees))}`} currency={currencySymbol} />        
                </div>
              </div>
            
              <div className="flex flex-wrap gap-3 mt-5">
                {cart?.products?.map((product, index) => (
                  <img
                    key={index}
                    src={product.img}
                    alt={`product-${index}`}
                    className="object-cover w-20 h-20 rounded-md shadow-md"
                  />
                ))}
              </div>
            </div>

            {/* Mobile */}
            <div className="flex flex-col gap-4 p-2 bg-white border border-gray-500 md:hidden">
              <CustomBannerShipment orderNumber={cart?.referenceCode} cartID={cart._id} company={cart.shippingCompany} companiesChoose={shipmentCosts[cart._id]?.data} getCompany={setGetCompany} loading={setLoading}/>
              <div className='flex justify-between'>   
                <div className='flex flex-col w-1/2 gap-4'>
                    <CustomTitleAndSubTitle title={t("from")} subTitle={cart?.area === "67dfef4a100f542a2c700c04" ? t("Turkey") : t("Europa")} />
                    <CustomTitleAndSubTitle title={t("PackagePrice")} subTitle={`${roundToTwo(Number(cart?.totalPackingFees))}`} currency={currencySymbol} />        
                  </div>
                  <div className='flex flex-col gap-4 '>
                    {cart.isPacking ?
                      <CustomTitleAndSubTitle title={t("WeightAfterPackaging")} subTitle={`${roundToTwo(Number(cart?.totalWeightAfterPacking))}`} currency={t("kg")} />
                      :
                      <CustomTitleAndSubTitle title={t("weight")} subTitle={`${roundToTwo(Number(cart?.totalWeight))}`} currency={t("kg")} />
                    }
                  </div>
              </div>

              <div className="flex flex-wrap gap-2 lg:gap-4">
                {cart?.products?.map((product, index) => (
                  <img
                    key={index}
                    src={product.img}
                    alt={`product-${index}`}
                    className="object-cover w-20 h-20 rounded-md shadow-md"
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Shipping Address */}
      <div className="w-full">
        <div className="flex my-5">
          <span className="pointer-events-none"><MapPinIcon className="w-8 h-8 text-black" /></span>
          <h2 className="text-lg font-semibold text-black md:text-2xl lg:text-3xl">{t('ShippingAddress')}</h2>
        </div>
        <div className='flex flex-col gap-5'>
          <div className="flex gap-3">
            <CustomInfoInput
              id="firstName"
              placeholder={t("FirstName")}
              label={t("FirstName")}
              type="text"
              value={mergedCart?.address?.firstName}
              autoComplete="off"
              icon={<i className="fas fa-user"></i>}
              readOnly
            />
            <CustomInfoInput
              id="lastName"
              placeholder={t("LastName")}
              label={t("LastName")}
              type="text"
              value={mergedCart?.address?.lastName}
              autoComplete="off"
              icon={<i className="fas fa-user"></i>}
              readOnly
            />
          </div>
          <div className="flex">
            <CustomInfoInput
              id="fullAddress"
              label={t("FullAddress")}
              type="text"
              placeholder={t("FullAddress")}
              value={mergedCart?.address?.fullAddress}
              autoComplete="off"
              icon={<i className="fas fa-map-marked-alt"></i>}
              readOnly
            />
          </div>
          <div className="flex gap-3">
            <CustomInfoInput
              id="country"
              label={t("Country")}
              type="text"
              placeholder={t("Country")}
              value={menuItemsLocationTo.find((item) => item.id === mergedCart?.address?.country)?.name || t("calculateShippingSection.selectCountry")}
              autoComplete="off"
              readOnly
            />
            <CustomInfoInput
              id="city"
              label={t("City")}
              type="text"
              placeholder={t("City")}
              value={mergedCart?.address?.city}
              autoComplete="off"
              icon={<i className="fas fa-city"></i>}
              readOnly
            />
          </div>
          <div className="flex gap-3">
            <CustomInfoInput
              id="postalCode"
              label={t("PostalCode")}
              type="text"
              placeholder={t("PostalCode")}
              value={mergedCart?.address?.postalCode}
              autoComplete="off"
              icon={<i className="fas fa-mail-bulk"></i>}
              readOnly
            />
            <CustomInfoInput
              id="state"
              label={t("State/Province")}
              type="text"
              placeholder={t("State")}
              value={mergedCart?.address?.state}
              autoComplete="off"
              icon={<i className="fas fa-road"></i>}
              readOnly
            />
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default ManageBoxDetails;